import {
  SMB_FY_QUARTER,
  SMB_GUIDED_GROWTH_DEFAULT,
  SMB_GUIDED_GROWTH_MAX,
  SMB_GUIDED_GROWTH_MIN,
  SMB_QUARTERS,
} from '../configs'
import { IPlanningSmbGuidedContext, IPlanningSmbGuidedDraft } from '../model/guidedWizardTypes'
import { IPlanningSmb, ISmbNode, TSmbSplitStore } from '../model/types'

import { applySmbEditToStore } from './applySmbEdit'
import { flattenSmbTree, getSmbAncestors, getSmbNodeId } from './buildSmbTree'
import { getSmbEffectiveValue } from './getSmbEffectiveValue'
import { getGuidedSmbPlanResult } from './guidedSmbPlanning'
import { isSmbNodeExcluded } from './isSmbNodeExcluded'
import { mapPlanningSmbToStore, mapStoreToPlanningSmbRows } from './mapPlanningSmb'
import { getPlanningSmbYears } from './planningSmbYears'
import {
  getDirectStoredSplit,
  getSmbStoreKey,
  normalizeSmbPercent,
  setStoredSplit,
} from './smbSplitStore'

export const normalizeGuidedSmbGrowth = (growth: number) =>
  Math.round(
    Math.max(
      SMB_GUIDED_GROWTH_MIN,
      Math.min(SMB_GUIDED_GROWTH_MAX, Number.isFinite(growth) ? growth : 0)
    ) * 10
  ) / 10

export const getGuidedSmbYears = getPlanningSmbYears

export const getGuidedSmbReference = (context: IPlanningSmbGuidedContext) =>
  !context.offeringBaseline.isEmpty
    ? context.offeringBaseline.smb
    : !context.offeringSummary.isEmpty
      ? context.offeringSummary.smb
      : 50

const getNodeReference = (context: IPlanningSmbGuidedContext, node: ISmbNode) => {
  for (const candidate of [node, ...getSmbAncestors(context.tree, node.id).reverse()]) {
    const baseline = context.getBaselineValue(candidate)
    if (!baseline.isEmpty) {
      return baseline.smb
    }
  }

  return getGuidedSmbReference(context)
}

const makeGeneratedStore = (context: IPlanningSmbGuidedContext, growth: number) => {
  const store: TSmbSplitStore = {}
  const visit = (node: ISmbNode) => {
    if (isSmbNodeExcluded(node)) {
      return
    }
    const children = node.children.filter((child) => !isSmbNodeExcluded(child))
    if (node.children.length && !children.length && node.settingsIndex < 0) {
      return
    }
    if (children.length) {
      children.forEach(visit)
      return
    }

    getGuidedSmbYears(context.fYear, context.lastPlanYear).forEach((year, index) => {
      setStoredSplit(
        store,
        node.id,
        year,
        SMB_FY_QUARTER,
        getGuidedSmbPlanResult(getNodeReference(context, node), growth * (index + 1)).smb
      )
    })
  }
  visit(context.tree)
  return store
}

/** Every preview and saved FY uses the same financial weights as the classic editor. */
export const buildGuidedSmbWizardStore = (
  context: IPlanningSmbGuidedContext,
  draft: IPlanningSmbGuidedDraft
): TSmbSplitStore => {
  const store = makeGeneratedStore(context, draft.growth)
  const years = getGuidedSmbYears(context.fYear, context.lastPlanYear)

  flattenSmbTree(context.tree).forEach((node) => {
    if (isSmbNodeExcluded(node)) {
      return
    }
    years.forEach((year) => {
      const entry = draft.overrides[getSmbStoreKey(node.id, year)]
      if (!entry) {
        return
      }
      const quarters = year === context.fYear ? SMB_QUARTERS : []
      for (const fq of [SMB_FY_QUARTER, ...quarters]) {
        const value = entry[fq]
        if (value) {
          if (
            fq !== SMB_FY_QUARTER &&
            !getDirectStoredSplit(store, node.id, year, SMB_FY_QUARTER)
          ) {
            const annual = getGuidedSmbWizardValue(context, store, node, year)
            if (!annual.isEmpty)
              setStoredSplit(store, node.id, year, SMB_FY_QUARTER, annual.smb, true)
          }
          applySmbEditToStore(store, {
            node,
            ancestors: getSmbAncestors(context.tree, node.id),
            year,
            fq,
            smb: value.smb,
            getWeight: context.getWeight,
            deriveFyFromQuarters: year === context.fYear,
          })
        }
      }
    })
  })

  return store
}

export const getGuidedSmbWizardValue = (
  context: IPlanningSmbGuidedContext,
  store: TSmbSplitStore,
  node: ISmbNode,
  year: number,
  fq = SMB_FY_QUARTER
) => {
  for (const candidate of [node, ...getSmbAncestors(context.tree, node.id).reverse()]) {
    const result = getSmbEffectiveValue(store, candidate, year, fq, context.getWeight, {
      deriveFyFromQuarters: year === context.fYear && fq === SMB_FY_QUARTER,
    })
    if (!result.isEmpty) {
      return result
    }
  }
  return { isEmpty: true } as const
}

export const createGuidedSmbWizardDraft = (
  context: IPlanningSmbGuidedContext
): IPlanningSmbGuidedDraft => {
  const years = getGuidedSmbYears(context.fYear, context.lastPlanYear)
  const loaded = mapPlanningSmbToStore(
    context.planningSmb,
    {
      partnerId: context.partnerId,
      programType: context.programType,
      years,
    },
    context.tree
  )
  const candidates: number[] = []
  for (const year of years) {
    for (const node of flattenSmbTree(context.tree)) {
      if (isSmbNodeExcluded(node)) {
        continue
      }
      const direct = getDirectStoredSplit(loaded, node.id, year, SMB_FY_QUARTER)
      if (direct) {
        candidates.push((direct.smb - getNodeReference(context, node)) / (year - context.fYear + 1))
      }
    }
    if (candidates.length) {
      break
    }
  }
  const fallbackGrowth = context.offeringSummary.isEmpty
    ? SMB_GUIDED_GROWTH_DEFAULT
    : context.offeringSummary.smb - getGuidedSmbReference(context)
  const growth = normalizeGuidedSmbGrowth(
    candidates.length && candidates.every((value) => Math.abs(value - candidates[0]) < 0.05)
      ? candidates[0]
      : fallbackGrowth
  )
  const generated = makeGeneratedStore(context, growth)
  const overrides: TSmbSplitStore = {}
  flattenSmbTree(context.tree).forEach((node) => {
    years.forEach((year) => {
      const entry = loaded[getSmbStoreKey(node.id, year)]
      if (!entry || isSmbNodeExcluded(node)) {
        return
      }
      Object.entries(entry).forEach(([quarter, value]) => {
        const fq = Number(quarter)
        if (!value) {
          return
        }
        const expected = getGuidedSmbWizardValue(context, generated, node, year, fq)
        if (
          fq !== SMB_FY_QUARTER ||
          expected.isEmpty ||
          normalizeSmbPercent(expected.smb) !== value.smb
        ) {
          setStoredSplit(overrides, node.id, year, fq, value.smb)
        }
      })
    })
  })
  return { growth, overrides }
}

/** A parent edit replaces its detail; sibling values and reset-to-growth remain independent. */
export const editGuidedSmbWizardDraft = (
  draft: IPlanningSmbGuidedDraft,
  node: ISmbNode,
  year: number,
  fq: number,
  smb: number | null
): IPlanningSmbGuidedDraft => {
  const overrides: TSmbSplitStore = Object.fromEntries(
    Object.entries(draft.overrides).map(([key, entry]) => [key, entry ? { ...entry } : entry])
  )
  const key = getSmbStoreKey(node.id, year)
  if (smb === null) {
    if (overrides[key]) {
      delete overrides[key]![fq]
      if (!Object.keys(overrides[key]!).length) {
        delete overrides[key]
      }
    }
  } else {
    if (fq === SMB_FY_QUARTER) {
      delete overrides[key]
    }
    flattenSmbTree(node)
      .slice(1)
      .forEach((child) => delete overrides[getSmbStoreKey(child.id, year)])
    setStoredSplit(overrides, node.id, year, fq, normalizeSmbPercent(smb))
  }
  return { ...draft, overrides }
}

export const getGuidedSmbWizardRows = (
  context: IPlanningSmbGuidedContext,
  store: TSmbSplitStore
): IPlanningSmb[] => {
  const nodes = new Map(flattenSmbTree(context.tree).map((node) => [node.id, node]))
  return mapStoreToPlanningSmbRows(
    store,
    context.tree,
    {
      partnerId: context.partnerId,
      programType: context.programType,
      years: getGuidedSmbYears(context.fYear, context.lastPlanYear),
    },
    { getWeight: context.getWeight, derivePlanYearFyFromQuarters: true }
  ).map((row) => {
    const node = nodes.get(
      getSmbNodeId(row.ProgramType, row.Category, row.SubCategory, row.L3Category)
    )
    return node?.children.length ? { ...row, IsCollapsed: 1 } : row
  })
}

export const isSameGuidedSmbRows = (left: IPlanningSmb[], right: IPlanningSmb[]) => {
  const signature = (rows: IPlanningSmb[]) =>
    rows
      .map((row) =>
        [
          row.PartnerID,
          row.ProgramType,
          row.Category,
          row.SubCategory || '',
          row.L3Category || '',
          row.FYear,
          row.FQuarter,
          normalizeSmbPercent(row.SMBPercent),
        ].join('|')
      )
      .sort()
      .join('\n')
  return signature(left) === signature(right)
}
